import { createFileRoute, Link } from "@tanstack/react-router";
import { PageHero } from "@/components/page-hero";
import { metaDesc } from "@/lib/seo-description";

const HERO = "https://images.unsplash.com/photo-1505373877841-8d25f7d46678?w=1600&q=72&auto=format&fit=crop";

export const Route = createFileRoute("/conditions-de-location")({
  head: () => ({
    meta: [
      { title: "Conditions de location de matériel | DJ Val" },
      {
        name: "description",
        content:
          metaDesc("Conditions générales de location de matériel sono, éclairage, photobooth et effets spéciaux : caution, livraison, responsabilité et annulation.")
      },
      { property: "og:title", content: "Conditions de location — DJ Val" },
      { property: "og:url", content: "https://dj-val.fr/conditions-de-location" },
    ],
    links: [{ rel: "canonical", href: "https://dj-val.fr/conditions-de-location" }],
  }),
  component: Page,
});

function Page() {
  return (
    <div className="bg-background text-muted-foreground">
      <PageHero
        image={HERO}
        eyebrow="CGV Location"
        title={<>Conditions <span className="italic">de location</span></>}
        subtitle="Conditions applicables à toute location de matériel sonorisation, éclairage, photobooth, karaoké, effets spéciaux et vidéo auprès de DJ Val, basé à Cavaillon (84300)."
        height="compact"
      />

      <section className="py-20 px-6 max-w-3xl mx-auto">
        <div className="space-y-8 text-sm leading-relaxed font-light">
          <div>
            <h2 className="font-serif-display text-xl text-foreground mb-3">1. Réservation</h2>
            <p>
              La réservation est confirmée à réception de l'acompte de <strong className="text-foreground">30 %</strong>{" "}
              du montant total et de la signature du contrat de location. Le solde est dû à la livraison ou au retrait
              du matériel.
            </p>
          </div>

          <div>
            <h2 className="font-serif-display text-xl text-foreground mb-3">2. Caution</h2>
            <p>
              Une caution est demandée à la signature du contrat, par chèque ou virement non encaissé. Son montant
              correspond à la valeur de remplacement à neuf du matériel loué. Elle est restituée intégralement au
              retour du matériel en bon état de fonctionnement.
            </p>
          </div>

          <div>
            <h2 className="font-serif-display text-xl text-foreground mb-3">3. Livraison, installation, retrait</h2>
            <p>Vous pouvez choisir :</p>
            <ul className="mt-3 space-y-2 list-disc list-inside marker:text-[var(--gold)]">
              <li>Le retrait à Cavaillon (gratuit, sur rendez-vous)</li>
              <li>La livraison sur site, avec installation et calage par nos soins</li>
              <li>L'installation + reprise complète (option « clé en main »)</li>
            </ul>
            <p className="mt-3">
              Les frais de livraison sont calculés depuis Cavaillon (forfait au-delà de 80 km). Aucun frais caché : le
              devis est ferme et définitif.
            </p>
          </div>

          <div>
            <h2 className="font-serif-display text-xl text-foreground mb-3">4. Durée de location</h2>
            <p>
              La location est facturée à la journée (24 h) ou au week-end (vendredi 17 h → lundi 10 h). Au-delà,
              chaque journée supplémentaire est facturée selon la grille tarifaire.
            </p>
          </div>

          <div>
            <h2 className="font-serif-display text-xl text-foreground mb-3">5. Responsabilité du locataire</h2>
            <p>
              Le locataire est responsable du matériel à compter de la livraison ou du retrait, jusqu'à sa restitution.
              Il s'engage à :
            </p>
            <ul className="mt-3 space-y-2 list-disc list-inside marker:text-[var(--gold)]">
              <li>Utiliser le matériel conformément à sa destination (notice fournie)</li>
              <li>Le protéger des intempéries, du vol, de la dégradation</li>
              <li>Signaler immédiatement tout incident ou panne</li>
            </ul>
            <p className="mt-3">
              En cas de casse, perte ou vol, le montant des réparations ou du remplacement à neuf est déduit de la
              caution (et facturé au-delà si nécessaire).
            </p>
          </div>

          <div>
            <h2 className="font-serif-display text-xl text-foreground mb-3">6. Assurance</h2>
            <p>
              Il est <strong className="text-foreground">vivement recommandé</strong> de vérifier que votre assurance
              habitation ou responsabilité civile événementielle couvre le matériel loué. Une attestation peut être
              demandée avant livraison pour les locations dépassant 3 000 €.
            </p>
          </div>

          <div>
            <h2 className="font-serif-display text-xl text-foreground mb-3">7. Annulation</h2>
            <ul className="space-y-2 list-disc list-inside marker:text-[var(--gold)]">
              <li>Plus de 30 jours avant l'événement : remboursement intégral de l'acompte</li>
              <li>Entre 30 et 7 jours : 50 % de l'acompte conservé</li>
              <li>Moins de 7 jours : acompte intégralement conservé</li>
            </ul>
            <p className="mt-3">
              En cas d'annulation pour cas de force majeure dûment justifié (intempérie majeure, événement sanitaire),
              l'acompte est intégralement reportable sur une autre date dans les 12 mois.
            </p>
          </div>

          <div>
            <h2 className="font-serif-display text-xl text-foreground mb-3">8. Sécurité — effets spéciaux</h2>
            <p>
              Les effets pyrotechniques froids (étincelles), CO₂, fumée lourde et machines à mousse sont soumis à des
              contraintes techniques que nous validons en amont avec votre lieu de réception (détecteurs incendie,
              ventilation, dégagements). Nous nous réservons le droit de refuser un effet incompatible avec le lieu.
            </p>
          </div>

          <p className="text-xs text-muted-foreground pt-6 border-t border-foreground/10">
            Voir aussi nos{" "}
            <Link to="/mentions-legales" className="text-[var(--gold)] hover:underline">mentions légales</Link> et notre{" "}
            <Link to="/politique-confidentialite" className="text-[var(--gold)] hover:underline">politique de confidentialité</Link>.
          </p>
        </div>
      </section>
    </div>
  );
}
